import { z } from "zod";
import type { ClientMessage } from "./generated/ClientMessage";
import type { HelpKind } from "./generated/HelpKind";
import type { Locale } from "./generated/Locale";
import type { Mood } from "./generated/Mood";
import type { SayChannel } from "./generated/SayChannel";

// Runtime validation for frames arriving from clients. The `satisfies` checks make
// this file fail to compile if it drifts from the types generated from Rust.

/** Longest message a child can send to the tutor, in UTF-16 code units. */
export const MAX_LEARNER_TEXT = 1000;
/** Longest answer to an item. */
export const MAX_ANSWER = 200;
/** Longest line of « Mon brouillon » (ADR-0052), in UTF-16 code units. */
export const MAX_WORK_LINE = 60;
/** Most lines « Mon brouillon » keeps per exercise. */
export const MAX_WORK_LINES = 8;
/** Longest answer to « Explique avec tes mots » (ADR-0059). */
export const MAX_TEACH_BACK = 280;

const clientMsgId = z.string().min(1).max(64);
const localeSchema = z.enum(["en", "fr"]) satisfies z.ZodType<Locale>;
const channelSchema = z.enum(["exercise", "curiosity"]) satisfies z.ZodType<SayChannel>;
const helpKindSchema = z.enum([
  "rephrase",
  "clue",
  "method",
  "example",
]) satisfies z.ZodType<HelpKind>;

const moodSchema = z.enum(["good", "meh", "hard"]) satisfies z.ZodType<Mood>;

export const clientMessageSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("learner.say"),
    clientMsgId,
    text: z.string().trim().min(1).max(MAX_LEARNER_TEXT),
    // Free questions (ADR-0042); absent from older clients, meaning the exercise.
    channel: channelSchema.nullable().exactOptional(),
    sttConfidence: z.number().min(0).max(1).exactOptional(),
  }),
  z.strictObject({
    type: z.literal("answer.submit"),
    clientMsgId,
    itemId: z.string().min(1).max(64),
    answer: z.string().min(1).max(MAX_ANSWER),
  }),
  z.strictObject({
    type: z.literal("item.request"),
    clientMsgId,
    locale: localeSchema,
    // « On reprend » (feature 3); absent from older clients, which never get it.
    opener: z.boolean().nullable().exactOptional(),
  }),
  z.strictObject({
    type: z.literal("session.pick"),
    clientMsgId,
    sessionStart: z.number().int().min(0).max(0xffff_ffff),
    // The actor checks the skill is on the session's plan.
    skillId: z.string().min(1).max(100),
    locale: localeSchema,
  }),
  z.strictObject({
    type: z.literal("session.end"),
    clientMsgId,
    sessionStart: z.number().int().min(0).max(0xffff_ffff),
    mood: moodSchema.nullable(),
  }),
  z.strictObject({
    type: z.literal("help.request"),
    clientMsgId,
    itemId: z.string().min(1).max(64),
    kind: helpKindSchema,
  }),
  z.strictObject({
    type: z.literal("work.write"),
    clientMsgId,
    itemId: z.string().min(1).max(64),
    text: z.string().trim().min(1).max(MAX_WORK_LINE),
  }),
  z.strictObject({
    type: z.literal("teachback.submit"),
    clientMsgId,
    itemId: z.string().min(1).max(64),
    text: z.string().trim().min(1).max(MAX_TEACH_BACK),
  }),
  z.strictObject({
    type: z.literal("lesson.answer"),
    clientMsgId,
    itemId: z.string().min(1).max(64),
    answer: z.string().trim().min(1).max(40),
  }),
  z.strictObject({
    type: z.literal("lesson.skip"),
    clientMsgId,
    itemId: z.string().min(1).max(64),
  }),
  z.strictObject({
    type: z.literal("item.advance"),
    clientMsgId,
    itemId: z.string().min(1).max(64),
  }),
  z.strictObject({
    type: z.literal("class.pick"),
    clientMsgId,
    // A programme section code (ADR-0038); the actor checks it is on the classe's list.
    section: z
      .string()
      .regex(/^fr\.c[34]\.[a-z]+(\.[a-z0-9]+(-[a-z0-9]+)*){1,3}$/u)
      .max(100)
      .nullable(),
  }),
]) satisfies z.ZodType<ClientMessage>;

export type ParseResult = { ok: true; message: ClientMessage } | { ok: false; error: string };

/** Parses and validates one raw WebSocket frame. Never throws. */
export function parseClientMessage(raw: string): ParseResult {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { ok: false, error: "frame is not valid JSON" };
  }
  const result = clientMessageSchema.safeParse(json);
  return result.success
    ? { ok: true, message: result.data }
    : { ok: false, error: z.prettifyError(result.error) };
}
